// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import type { GuidedSectionId, GuidedTranscriptEvent } from 'employment-insurance-data-access';
import type { GuidedTextResolver } from './guided-text';

const SECTION_IDS: Record<number, GuidedSectionId> = {
  1: 'absences',
  2: 'work',
  3: 'fullTimeJob',
  4: 'training',
  5: 'availability',
  6: 'otherMoney',
};

export interface TranscriptProps {
  events: GuidedTranscriptEvent[];
  t: GuidedTextResolver;
  label: string;
}

/**
 * The conversation so far, as the BFF's structured event log. A
 * `role="log"` region: assistive technology announces each event as it is
 * appended -- once -- and never re-reads what was already there, because
 * every event keeps a stable key (its id) across turns.
 */
export function Transcript({ events, t, label }: TranscriptProps) {
  return (
    <div className="ei-guided__transcript" role="log" aria-label={label} data-testid="guided-transcript">
      {events.map((event) => (
        <TranscriptEvent key={event.id} event={event} t={t} />
      ))}
    </div>
  );
}

function TranscriptEvent({ event, t }: { event: GuidedTranscriptEvent; t: GuidedTextResolver }) {
  switch (event.kind) {
    case 'question': {
      const section = SECTION_IDS[event.section];
      return (
        <div className="ei-guided__bubble ei-guided__bubble--question" data-kind="question" data-step={event.stepId}>
          <span className="ei-guided__eyebrow">
            {section ? `${event.section}. ${t({ key: `section.${section}` })}` : t({ key: 'navStart' })}
          </span>
          <p className="ei-guided__question-text">{t(event.text)}</p>
        </div>
      );
    }
    case 'answer':
      return (
        <div className="ei-guided__bubble ei-guided__bubble--answer" data-kind="answer">
          {t(event.text)}
        </div>
      );
    case 'agent':
      return (
        <div className="ei-guided__bubble ei-guided__bubble--agent" data-kind="agent">
          {t(event.text)}
        </div>
      );
    case 'note':
      return (
        <div className="ei-guided__note" data-kind="note">
          {t(event.text)}
        </div>
      );
    case 'flag':
      return (
        <div className="ei-guided__note ei-guided__note--flag" data-kind="flag" data-code={event.code}>
          <strong>{t(event.headline)}</strong> {t(event.detail)}
          {event.action ? (
            <>
              {' '}
              <em>{t(event.action)}</em>
            </>
          ) : null}
        </div>
      );
    case 'error':
      return (
        <div className="ei-guided__note ei-guided__note--error" data-kind="error">
          <strong>{t({ key: 'error.prefix' })}</strong> {t(event.text)}
        </div>
      );
  }
}
